import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductCategoriesQuery } from "src/graphql/generated/graphql";
import { productCategoriesQuery } from "src/graphql/operations/productCategories/queries";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";
import { unsafeTranslate } from "src/utils/helpers";

import { handleError } from "../utils/error";

export const useProductCategories = () => {
    const { t } = useTranslation(["common"]);

    const [productCategories, setProductCategories] = useState<ProductCategoriesQuery["productCategories"]["items"]>(
        []
    );
    const [productCategoriesTotal, setProductCategoriesTotal] = useState(0);
    const [isLoadingProductCategories, setIsLoadingProductCategories] = useState(false);

    const fetchProductCategories = useCallback(async (nextToken?: string) => {
        setIsLoadingProductCategories(true);

        let hasNext = false;
        try {
            const response = await graphqlFetch(productCategoriesQuery, { nextToken });

            setProductCategories((productCategories) => [
                ...productCategories,
                ...(response.productCategories?.items || []),
            ]);
            setProductCategoriesTotal(
                (productCategoriesTotal) => productCategoriesTotal + (response.productCategories?.items?.length || 0)
            );

            if (response.productCategories?.nextToken) {
                hasNext = true;
                fetchProductCategories(response.productCategories.nextToken);
            }
        } catch (err) {
            handleError(err, {
                message: { key: "productcategories_load_failed", namespace: "common" },
                context: { nextToken },
            });
        } finally {
            if (!hasNext) {
                setIsLoadingProductCategories(false);
            }
        }
    }, []);

    useEffect(() => {
        setProductCategories([]);
        setProductCategoriesTotal(0);
        fetchProductCategories();
    }, [fetchProductCategories]);

    const localizedProductCategories = useMemo(() => {
        return productCategories.map((productCategory) => ({
            ...productCategory,
            name: unsafeTranslate(`productcategory_${productCategory.key}`, t),
        }));
    }, [t, productCategories]);

    return {
        items: [],
        productCategories: localizedProductCategories,
        productCategoriesTotal,
        isLoadingProductCategories,
        reloadProducts() {
            setProductCategories([]);
            setProductCategoriesTotal(0);
            fetchProductCategories();
        },
    };
};
